import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getPriceService, usePrices } from '@hooks/usePrices';
import { RoundService } from '@services/roundService';
import type { Asset } from '@shared/types';
import { createOptionIdentifier, type OptionIdentifier } from '@/types/optionDiscovery';
import { brandColors } from '@utils/color-utils';
import { generateDynamicStrikePrices } from '@utils/chartHelpers';

interface OptionSelectorModalProps {
  isVisible: boolean;
  currentOption: OptionIdentifier | null;
  onClose: () => void;
  onSelect: (option: OptionIdentifier) => void;
}

const SUPPORTED_ASSETS: Asset[] = ['BTC', 'ETH', 'SOL'];

export function OptionSelectorModal({
  isVisible,
  currentOption,
  onClose,
  onSelect,
}: OptionSelectorModalProps) {
  const pricesQuery = usePrices();
  const prices = pricesQuery.data;
  const priceService = getPriceService();
  
  const [selectedAsset, setSelectedAsset] = useState<Asset>(
    currentOption?.asset ?? 'ETH',
  );
  const [selectedType, setSelectedType] = useState<'CALL' | 'PUT'>(
    currentOption?.type ?? 'CALL',
  );
  const [selectedStrike, setSelectedStrike] = useState<number>(
    currentOption?.strikePrice ?? 0,
  );

  const strikeListRef = useRef<HTMLDivElement>(null);
  const selectedStrikeRef = useRef<HTMLButtonElement | null>(null);
  
  // Get spot price from query data, or fallback to priceService directly
  const spotPriceFromQuery = prices?.[selectedAsset]?.price ?? 0;
  const spotPriceFromService = priceService.getPrice(selectedAsset)?.price ?? 0;
  const spotPrice = spotPriceFromQuery || spotPriceFromService;

  // Update selections when currentOption changes
  useEffect(() => {
    if (currentOption) {
      setSelectedAsset(currentOption.asset);
      setSelectedType(currentOption.type);
      setSelectedStrike(currentOption.strikePrice);
    }
  }, [currentOption]);
  const expiryTimestamp = RoundService.getCurrentRoundExpiry();

  const strikePrices = useMemo(() => {
    if (spotPrice <= 0) {
      return [];
    }
    return generateDynamicStrikePrices(spotPrice, selectedAsset);
  }, [spotPrice, selectedAsset]);

  // Track previous asset to detect asset changes
  const prevAssetRef = useRef<Asset | null>(null);

  // Scroll to selected strike when it changes or when modal opens
  useEffect(() => {
    if (isVisible && selectedStrikeRef.current && strikeListRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        selectedStrikeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isVisible, selectedStrike, strikePrices.length]);

  // Auto-update strike when asset changes (force strike change)
  useEffect(() => {
    // Only update if asset actually changed
    if (prevAssetRef.current !== null && prevAssetRef.current !== selectedAsset) {
      if (spotPrice > 0 && strikePrices.length > 0) {
        // Find the strike closest to the current spot price (at-the-money)
        const closestStrike = strikePrices.reduce((prev, curr) => {
          return Math.abs(curr - spotPrice) < Math.abs(prev - spotPrice)
            ? curr
            : prev;
        });
        setSelectedStrike(closestStrike);
      }
    }
    prevAssetRef.current = selectedAsset;
  }, [selectedAsset, spotPrice, strikePrices]);

  const handleConfirm = () => {
    if (selectedStrike > 0) {
      const option = createOptionIdentifier(
        selectedAsset,
        selectedStrike,
        selectedType,
        expiryTimestamp,
      );
      onSelect(option);
      onClose();
    }
  };

  const formatStrikePrice = (price: number): string => {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-4 top-20 bottom-auto max-h-[80vh] bg-brand-dark border border-white/10 rounded-lg z-50 flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10">
              <h2 className="text-white font-semibold text-lg">Select Option</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Asset Selection */}
              <div>
                <label className="text-white/60 text-xs mb-2 block">Asset</label>
                <div className="flex gap-2">
                  {SUPPORTED_ASSETS.map(asset => (
                    <motion.button
                      key={asset}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        selectedAsset === asset
                          ? 'bg-white/20 text-white'
                          : 'bg-white/10 text-white/60 hover:bg-white/15'
                      }`}
                      onClick={() => setSelectedAsset(asset)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {asset}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Type Selection */}
              <div>
                <label className="text-white/60 text-xs mb-2 block">Direction</label>
                <div className="flex gap-2">
                  <motion.button
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-bold uppercase transition-colors ${
                      selectedType === 'CALL'
                        ? 'bg-white/20'
                        : 'bg-white/10 hover:bg-white/15'
                    }`}
                    style={{
                      color:
                        selectedType === 'CALL'
                          ? brandColors.green
                          : brandColors.green + '80',
                    }}
                    onClick={() => setSelectedType('CALL')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    ABOVE
                  </motion.button>
                  <motion.button
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-bold uppercase transition-colors ${
                      selectedType === 'PUT'
                        ? 'bg-white/20'
                        : 'bg-white/10 hover:bg-white/15'
                    }`}
                    style={{
                      color:
                        selectedType === 'PUT'
                          ? brandColors.red
                          : brandColors.red + '80',
                    }}
                    onClick={() => setSelectedType('PUT')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    BELOW
                  </motion.button>
                </div>
              </div>

              {/* Strike Price Selection */}
              <div>
                <label className="text-white/60 text-xs mb-2 block">
                  Strike Price
                </label>
                {strikePrices.length === 0 ? (
                  <div className="px-4 py-8 text-center text-white/40 text-sm">
                    {pricesQuery.isLoading || spotPrice <= 0
                      ? 'Loading strike prices...'
                      : 'No strike prices available'}
                  </div>
                ) : (
                  <div
                    ref={strikeListRef}
                    className="max-h-[300px] overflow-y-auto space-y-1"
                  >
                    {strikePrices.map(strike => (
                      <motion.button
                        key={strike}
                        ref={selectedStrike === strike ? selectedStrikeRef : null}
                        className={`w-full px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                          selectedStrike === strike
                            ? 'bg-white/20 text-white'
                            : 'bg-white/5 text-white/60 hover:bg-white/10'
                        }`}
                        onClick={() => setSelectedStrike(strike)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        ${formatStrikePrice(strike)}
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 flex gap-2">
              <motion.button
                className="flex-1 px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/15 transition-colors"
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                className="flex-1 px-4 py-2 bg-white text-brand-dark rounded-lg text-sm font-semibold hover:bg-white/90 transition-colors"
                onClick={handleConfirm}
                disabled={selectedStrike <= 0}
                whileHover={{ scale: selectedStrike > 0 ? 1.02 : 1 }}
                whileTap={{ scale: selectedStrike > 0 ? 0.98 : 1 }}
              >
                Select
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

